import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { AtLeast } from '#layers/base/public';
import type { AnyItem, CompositePrimaryKey, ConnectToMany, DisconnectToMany, FieldFilter, Filter, LogicalFilterOR, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { CreateItem, RelationFormFieldContext } from '#layers/forms/public';
import { computed, toValue } from 'vue';
import { isNullishOrEmpty } from '#layers/base/public';
import { useDisplayTemplateFieldSelection } from '#layers/blocks/composables/use-display-template-field-selection';
import { injectConstraintFields } from '#layers/blocks/utils/relation/inject-constraint-fields';
import { useItems } from '#layers/collection-query/public';
import { buildFilterFromItemKeys, getCollectionPrimaryKeyFields, getRelationConstraint, getRelationPrimitiveFields, isConnectToMany, isCreateToMany, isDisconnectToMany, isEmptyFilter, isSameKey, isUpdateToMany, pickByPrimitiveFields } from '#layers/data-model/public';
import { getOriginalItemKey, injectFormBlockContext, isCreatedItem } from '#layers/forms/public';

type RequiredFields = 'field' | 'referencedItemKey' | 'fieldValue';

export interface UseO2MStateOptions extends AtLeast<RelationFormFieldContext<'to-many'>, RequiredFields> {
	pageSize?: MaybeRefOrGetter<number | undefined>;
}

export interface O2MStateHandlers {
	createItem: (item: AnyItem) => void;
	undoCreateItem: (item: CreateItem) => void;
	connectItem: (key: CompositePrimaryKey) => void;
	undoConnectItem: (key: CompositePrimaryKey) => void;
	disconnectItem: (key: CompositePrimaryKey) => void;
	undoDisconnectItem: (key: CompositePrimaryKey) => void;
	updateItem: (originalKey: CompositePrimaryKey | null, item: AnyItem) => void;
}

export interface UseO2MStateReturn extends O2MStateHandlers {
	collection: ComputedRef<ResolvedMonospaceCollection | undefined>;
	items: ComputedRef<AnyItem[]>;
	selectedItemsFilter: ComputedRef<Filter>;
	getItemKey: (item: AnyItem) => CompositePrimaryKey | null;
}

export function useO2MState(options: UseO2MStateOptions) {
	const { referencedItemKey, referencedItemReferenceKey, fieldValue, pageSize } = options;

	const computedField = computed(() => toValue(options.field));
	const constraint = computed(() => computedField.value ? getRelationConstraint(computedField.value).constraint : null);
	const oppositeCollection = computed(() => computedField.value?.oppositeCollection);
	const oppositeCollectionPrimaryKeyFields = computed(() => oppositeCollection.value ? getCollectionPrimaryKeyFields(oppositeCollection.value) : null);

	const sourceReferenceKey = computed(() => referencedItemReferenceKey === undefined
		? toValue(referencedItemKey)
		: toValue(referencedItemReferenceKey));

	// In create mode the parent isn't persisted and its reference key is the live form value, so a
	// persisted-connections filter would change on every keystroke (refetching and flashing the
	// pagination controls)
	const formBlockContext = injectFormBlockContext(null);
	const isPersistedParent = computed(() => !(formBlockContext?.isCreate.value ?? false));

	const connectedItemsFilter = computed<FieldFilter | undefined>(() => {
		const referencedItemKeyValue = sourceReferenceKey.value;

		if (!referencedItemKeyValue) {
			return;
		}

		const mappedKeys: CompositePrimaryKey = {};

		for (const { constrainedField, referencedField } of constraint.value!.fieldMappings) {
			const value = referencedItemKeyValue[referencedField!.apiName];

			// A new/unsaved parent has no reference value yet — an untouched field reads as `''` (or
			// `null`). Without this guard the filter degrades to `_eq: ''`/`_eq: null`, which matches
			// every unparented child, so all orphaned rows wrongly render as already connected.
			if (isNullishOrEmpty(value)) {
				return;
			}

			mappedKeys[constrainedField!.apiName] = value;
		}

		// A composite reference key maps several FK fields; every one must match, so return one
		// FieldFilter carrying all conditions (implicit AND) rather than only the first field.
		return Object.fromEntries(
			Object.entries(mappedKeys).map(([key, keyValue]) => [key, { _eq: keyValue }]),
		);
	});

	function undoConnectItem(itemKey: CompositePrimaryKey) {
		options.emitUndoConnect?.(itemKey);
	}

	function undoDisconnectItem(itemKey: CompositePrimaryKey) {
		options.emitUndoDisconnect?.(itemKey);
	}

	function connectItem(itemKey: CompositePrimaryKey) {
		undoDisconnectItem(itemKey);
		options.emitConnect?.(itemKey);
	}

	function disconnectItem(itemKey: CompositePrimaryKey) {
		undoConnectItem(itemKey);
		options.emitDisconnect?.(itemKey);
	}

	function createItem(item: AnyItem) {
		options.emitCreate?.(item);
	}

	function undoCreateItem(item: CreateItem) {
		options.emitUndoCreate?.(item);
	}

	function updateItem(originalKey: CompositePrimaryKey | null, item: AnyItem) {
		options.emitUpdate?.(originalKey, item);
	}

	function getItemKey(item: AnyItem) {
		return getOriginalItemKey(item) ?? (oppositeCollectionPrimaryKeyFields.value ? pickByPrimitiveFields(item, oppositeCollectionPrimaryKeyFields.value) : null);
	}

	const newlyConnectedKeys = computed(() => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return [];
		}

		return fieldValue.value
			.filter((operation) => isConnectToMany(operation))
			.flatMap((operation: ConnectToMany) => operation._connect.keys);
	});

	const newlyConnectedItemsFilter = computed<Filter>(() => {
		return buildFilterFromItemKeys({
			keys: newlyConnectedKeys.value,
			primaryKeyFields: oppositeCollectionPrimaryKeyFields.value ?? [],
		});
	});

	const newlyDisconnectedKeys = computed(() => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return [];
		}

		return fieldValue.value
			.filter((operation) => isDisconnectToMany(operation))
			.map((operation: DisconnectToMany) => operation._disconnect.filter) as CompositePrimaryKey[];
	});

	const updateOps = computed(() => fieldValue.value?.filter((op) => isUpdateToMany(op)) ?? []);

	const selectedItemsFilter = computed(() => {
		const and: Filter[] = [];
		let orFilter: LogicalFilterOR | null = null;

		function or(filter: Filter) {
			if (orFilter === null) {
				orFilter = { _or: [] };
				and.push(orFilter);
			}

			orFilter._or.push(filter);
		}

		if (isPersistedParent.value && connectedItemsFilter.value && !isEmptyFilter(connectedItemsFilter.value)) {
			or(connectedItemsFilter.value);
		}

		if (!isEmptyFilter(newlyConnectedItemsFilter.value)) {
			or(newlyConnectedItemsFilter.value);
		}

		return {
			_and: and,
		};
	});

	const filterEmpty = computed(() => isEmptyFilter(selectedItemsFilter.value));

	const displayTemplateFieldSelection = useDisplayTemplateFieldSelection(oppositeCollection);

	const fieldSelection = computed(() => {
		const field = computedField.value;
		if (!field) return displayTemplateFieldSelection.value;
		return injectConstraintFields(displayTemplateFieldSelection.value, getRelationPrimitiveFields(field).oppositeFields);
	});

	const remoteItems = useItems({
		collection: oppositeCollection,
		enabled: () => !!computedField.value && !filterEmpty.value,
		query: () => ({
			filter: selectedItemsFilter.value,
			...fieldSelection.value,
		}),
		pageSize,
	});

	const createdItems = computed(() => !isNullishOrEmpty(fieldValue.value)
		? fieldValue.value
				.filter((op) => isCreateToMany(op))
				.flatMap((op) => op._create.data)
		: []);

	const items = computed(() => {
		if (remoteItems.pageNumber.value === 1) {
			return [
				...remoteItems.items.value,
				...createdItems.value,
			];
		}
		else {
			return remoteItems.items.value;
		}
	});

	const itemsWithUpdateEdits = computed(() => items.value.map((item) => {
		if (isNullishOrEmpty(fieldValue.value)) {
			return item;
		}

		const itemEdits = updateOps.value.find((op) => isSameKey(op._update.filter as CompositePrimaryKey, getItemKey(item)));

		if (itemEdits && isUpdateToMany(itemEdits)) {
			return { ...item, ...itemEdits._update.data };
		}

		return item;
	}));

	const totalItemsCount = computed(() => {
		if (remoteItems.totalItemsCount.value != null) {
			return remoteItems.totalItemsCount.value + createdItems.value.length;
		}
	});

	const currentItemsCount = computed(() => remoteItems.currentItemsCount.value + createdItems.value.length);

	// Unsaved rows are rendered on page one and belong to no server page, so the pagination controls
	// have to subtract them back out of the counts above.
	const unpagedItemsCount = computed(() => createdItems.value.length);

	// Autorefresh on submit
	// The engine doesn't return the results
	if (formBlockContext) {
		formBlockContext.state.value?.$onSuccess?.(() => {
			remoteItems.refresh();
		});
	}

	// eslint-disable-next-line unicorn/consistent-function-scoping
	function isNewlyCreated(item: object): boolean {
		return isCreatedItem(item);
	}

	function isNewlyUpdated(item: object): boolean {
		const key = getItemKey(item);
		return key && updateOps.value.some((updateOp) => isSameKey(updateOp._update.filter as CompositePrimaryKey, key));
	}

	function isNewlyConnected(item: object): boolean {
		const key = getItemKey(item);
		return key && newlyConnectedKeys.value.some((connectedKey) => isSameKey(connectedKey, key));
	}

	function isNewlyDisconnected(item: object): boolean {
		const key = getItemKey(item);
		return key && newlyDisconnectedKeys.value.some((disconnectedKey) => isSameKey(disconnectedKey, key));
	}

	return {
		collection: oppositeCollection,
		items: itemsWithUpdateEdits,
		pageNumber: remoteItems.pageNumber,
		totalItemsCount,
		currentItemsCount,
		unpagedItemsCount,
		isLoading: remoteItems.isLoading,
		createItem,
		undoCreateItem,
		connectItem,
		undoConnectItem,
		disconnectItem,
		undoDisconnectItem,
		updateItem,
		selectedItemsFilter,
		getItemKey,
		isNewlyCreated,
		isNewlyUpdated,
		isNewlyConnected,
		isNewlyDisconnected,
	};
}
